/**
 * ship-draw.ts — the canonical ship rendering seam (v2M7.1, GRAPHICS.md §4.1).
 *
 * Designed as a flat snapshot interface so v3M8 can replace the body of
 * `drawShip` with a sprite-atlas blit keyed on `(chassisId, race)` without
 * touching any caller. The renderer hands in a frame-snapshot; ship-draw
 * decides how to paint it.
 *
 * v2M7.1 ships the existing chevron unchanged — pure refactor, zero visual
 * change. v2M7.2 swaps the chevron for per-race silhouettes (RACE-AA / BB
 * / CC). v2M7.3 layers damage / shield / cloak overlays on top using the
 * `hullPct` / `shieldPct` / `cloakPhase` fields. Each ladder rung lands
 * inside this file so the seam stays coherent.
 *
 * Architectural rules (must hold through v3M8):
 *  - No imports from `src/state/*` or `src/game/*`. The function takes a
 *    flat input record; it never reaches into GameState.
 *  - x / y are screen-space pixels at the centre of the ship — the caller
 *    has already done the camera transform. (GRAPHICS.md §4.1's "system-
 *    view world coords" reads as "the function's coordinate frame is what
 *    the caller passed in"; the current call site passes screen-space.)
 *  - Side-effect-free except for ctx mutations bracketed by save / restore.
 *  - Forward-compat fields (`heading`, `hullPct`, `shieldPct`, `cloakPhase`,
 *    `dimensions`) are accepted by the contract today even though the v2M7.1
 *    chevron doesn't read all of them — that way the call site shape stays
 *    stable across the v2M7.2-.5 evolution.
 */

import type { RaceId } from '../state/types/index';
import {
  getColoredShipSprite,
  getShipSprite,
  getTintedShipSprite,
  SHIP_SPRITE_RENDER_SCALE,
} from './ship-sprites';
import { paletteIdFromHex } from '../state/empire-colors';
import {
  getSpaceStructureSprite,
  getStructureSpriteScale,
} from './space-structure-sprites';

export interface ShipRenderInput {
  /** Chassis name, e.g. 'Scout', 'Fighter', 'Cruiser' (matches Ships.xlsx). */
  chassisId: string;
  /** Owning empire's race. */
  race: RaceId;
  /** Screen-space centre, in pixels. */
  x: number;
  y: number;
  /** Heading in radians; 0 = +x axis. v2M7.2 reads this for silhouette rotation. */
  heading: number;
  /** Hull fraction 0..1. v2M7.3 reads this for damage overlay. */
  hullPct: number;
  /** Shield fraction 0..1. v2M7.3 reads this for shield halo. */
  shieldPct: number;
  /** Cloak shimmer phase 0..1, or null when uncloaked. v2M7.3 + §9.3 use this. */
  cloakPhase: number | null;
  /** Selected by the player (drives the selection ring). */
  selected: boolean;
  /**
   * Chassis dimensions in *world* units (matches Ships.xlsx Length / Width
   * columns when those land). v2M7.2.5 derives them from `classLevel`
   * since Ships.xlsx Length/Width columns don't exist yet; v3 reads from
   * the spreadsheet directly. The field is in the contract for forward-
   * compat.
   */
  dimensions: { length: number; width: number };
  /**
   * Ship class 1..6 (matches `ShipTemplate.classLevel`). v2M7.2.5 uses
   * this to scale the silhouette: Class 1 = 0.85×, Class 6 = 1.6×.
   * Future: v3 reads `dimensions` directly from Ships.xlsx and this
   * field becomes redundant.
   */
  classLevel: number;
}

/**
 * Pixel radius the silhouette draws within. Per-race silhouettes in
 * v2M7.2 use this as the half-extent for hull geometry; selection /
 * cloak rings sit just outside.
 */
export const SHIP_DRAW_RADIUS = 7;

/**
 * Per-race accent colours per GRAPHICS §2.2. Used as the silhouette fill
 * so ownership reads at a glance (per §2.3 "race colour space reserved
 * for ownership"). The accent is also the engine-glow colour in §4.3 —
 * for v2M7.2 we use the same value for both, keeping the per-race read
 * unmistakable. Hull-highlight from §2.1 sits as the edge-stroke.
 *
 * v2 races RACE-DD / RACE-EE deferred — fall back to RACE-AA's blue if a
 * v2-deferred race somehow appears in v2M7-era play (defensive only).
 */
export const RACE_ACCENT: Readonly<Record<RaceId, string>> = Object.freeze({
  'RACE-AA': '#3FA7E8', // Terran steel-blue
  'RACE-BB': '#C1434A', // Kargath blood-red
  'RACE-CC': '#7A4DC7', // Reman stealth-violet
  'RACE-DD': '#3FA7E8',
  'RACE-EE': '#3FA7E8',
  // v3M21.1 — synthetic neutral empire owns no ships; key exists
  // only to satisfy Record<RaceId, …>. Defensive-grey fallback in
  // case a future code path accidentally renders a neutral-owned ship.
  Neutral: '#7a8bb5',
});

/** Edge-highlight stroke colour (GRAPHICS §2.1 Hull-highlight). */
const HULL_HIGHLIGHT = '#C5CEDB';

/** Damage overlay colour — silhouette stroke shifts toward this as hull drops (GRAPHICS §2.1 Hull-red). */
const HULL_RED = '#D24548';

/** Shield halo colour (GRAPHICS §2.1 Shield-blue). */
const SHIELD_BLUE = '#4FB3FF';

/**
 * Linear interpolation between two `#RRGGBB` colours, returning a fresh
 * `#RRGGBB` string. `t` clamped to [0, 1]. Cheap enough to call per-ship
 * per-frame; if hot-path profiling ever shows it, pre-compute a lookup
 * table.
 */
function lerpHex(a: string, b: string, t: number): string {
  const tt = Math.max(0, Math.min(1, t));
  const ar = parseInt(a.slice(1, 3), 16);
  const ag = parseInt(a.slice(3, 5), 16);
  const ab = parseInt(a.slice(5, 7), 16);
  const br = parseInt(b.slice(1, 3), 16);
  const bg = parseInt(b.slice(3, 5), 16);
  const bb = parseInt(b.slice(5, 7), 16);
  const r = Math.round(ar + (br - ar) * tt);
  const g = Math.round(ag + (bg - ag) * tt);
  const bl = Math.round(ab + (bb - ab) * tt);
  return (
    '#' +
    r.toString(16).padStart(2, '0') +
    g.toString(16).padStart(2, '0') +
    bl.toString(16).padStart(2, '0')
  );
}

/**
 * Pick the silhouette stroke colour given the ship's hull fraction. At
 * full hull the stroke is HULL_HIGHLIGHT (clean spec line); as hull
 * drops the stroke interpolates toward HULL_RED, so a damaged ship
 * reads as "red-rimmed" at a glance. v2M7.3 lerp shape — v3M10 balance
 * may swap for a step function (e.g. clean → amber at 50 % → red at
 * 25 %) if the smooth blend reads ambiguously in playtest.
 */
function hullStrokeColour(hullPct: number): string {
  return lerpHex(HULL_HIGHLIGHT, HULL_RED, 1 - hullPct);
}

/** Look up the canonical accent for an empire's race. */
export function raceAccentFor(race: RaceId): string {
  return RACE_ACCENT[race];
}

/**
 * Visual role of a chassis — drives the small "add-on" overlay drawn on
 * top of the per-race silhouette so a Scout reads differently from a
 * Coloniser even though both share the Terran trapezoid base.
 *
 * Classification is name-pattern-based so new race-equivalent chassis
 * (e.g. another race's "Reaver" combat-light) don't need an explicit
 * map entry — the regex catches the family. Unknown chassis fall back
 * to 'combat-light' so they still render as something sensible.
 */
export type ShipRole =
  | 'scout'
  | 'colony'
  | 'utility'
  | 'combat-light'
  | 'combat'
  | 'capital'
  | 'space-structure'
  | 'squadron';

export function chassisRoleFor(chassisId: string): ShipRole {
  // v3M17 squadrons — synthetic chassis names ending in "Squadron"
  // (Corvette Squadron / Fighter Squadron / Infiltrator Squadron).
  // Checked first so a 'Corvette Squadron' doesn't match the
  // `Corvette` combat-light branch.
  if (/Squadron$/i.test(chassisId)) return 'squadron';
  // Light recon — Scout (Terran), Ranger (Kargath), Probe (Reman).
  if (/^(Scout|Ranger|Probe)$/i.test(chassisId)) return 'scout';
  // Coloniser (deferred from starter fleets but present in chassis catalogue).
  if (/Colon[iy]ser/i.test(chassisId)) return 'colony';
  // Constructor family — Engineer / Constructor / Mechanic
  // (CONSTRUCTOR_CHASSIS_BY_RACE in balance-space-structures.ts).
  if (/^(Engineer|Constructor|Mechanic)$/i.test(chassisId)) return 'utility';
  // Light combat — Corvette / Fighter / Infiltrator (Class 1 combat).
  if (/^(Corvette|Fighter|Infiltrator)$/i.test(chassisId)) return 'combat-light';
  // Mid combat — Cruiser / Praetorian / Warship / Destroyer (Class 2-3).
  if (/^(Cruiser|Praetorian|Warship|Destroyer)$/i.test(chassisId)) return 'combat';
  // Capital — Battleship / Dreadnought / Colossus / Carrier (Class 4-6).
  if (/^(Battleship|Dreadnought|Colossus|Carrier)$/i.test(chassisId)) return 'capital';
  // Space structures (STRUCTURES §3 "stationary ships" classification).
  // MFix11.0 (2026-05-15) — added \b word boundaries. Without them the
  // `Gate` pattern matches inside "Frigate", routing Assault Frigate +
  // Missile Frigate through the space-structure draw path (hexagonal
  // placeholder glyph) and bypassing the v4M4.a sprite fast path.
  // Latent bug since v3M2 added Warp Gate to the space-structure list;
  // only became visible at v4M4.a when commissioned sprites made the
  // placeholder-vs-bitmap difference obvious.
  if (/\b(Buoy|Platform|Yard|Outpost|Refinery|Gate|Minefield)\b/i.test(chassisId)) {
    return 'space-structure';
  }
  // Unknown chassis — default to combat-light so it renders as something.
  return 'combat-light';
}

/**
 * Per-class size scale for the silhouette. Class 1 renders at 75 % of
 * the canonical ship size; Class 6 at 200 % — so a Class 6 capital is
 * 2.67× the screen footprint of a Class 1 corvette. Linear +0.25 per
 * class for clean, predictable progression.
 *
 * v2M7.2.5 first pass used 0.85 → 1.6 (15 % step) — Class 1 vs Class 2
 * read as the same size in playtest. Bumped to 0.75 → 2.0 (25 % step)
 * for v2M7.2.6 so adjacent classes are unmistakably distinct. Tunable
 * in v3M10 balance pass.
 */
const CLASS_SCALE: Readonly<Record<1 | 2 | 3 | 4 | 5 | 6, number>> = Object.freeze({
  1: 0.75,
  2: 1.0,
  3: 1.25,
  4: 1.5,
  5: 1.75,
  6: 2.0,
});

function classScale(classLevel: number): number {
  const clamped = Math.max(1, Math.min(6, Math.round(classLevel))) as 1 | 2 | 3 | 4 | 5 | 6;
  return CLASS_SCALE[clamped];
}

/**
 * Paint a single ship into `ctx` at `input.{x,y}` using `accent` as the
 * primary fill colour. The accent corresponds to the empire's race accent
 * per GRAPHICS §2.3 (today the call site passes the per-empire colour
 * from `Empire.color`; v2M7.2 may switch to a per-race accent constant).
 *
 * Render order: selection ring (if selected) → own-cloak indicator (if
 * cloakPhase != null) → silhouette body. Selection and cloak rings sit
 * just outside the body radius so they read as decoration rather than
 * occluding the unit itself.
 */
export function drawShip(
  ctx: CanvasRenderingContext2D,
  input: ShipRenderInput,
  accent: string,
): void {
  const { x, y, selected, cloakPhase } = input;
  const cloaked = cloakPhase !== null;

  if (selected) {
    ctx.strokeStyle = '#ffe08a';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, SHIP_DRAW_RADIUS + 4, 0, Math.PI * 2);
    ctx.stroke();
  }

  if (cloaked) {
    ctx.save();
    ctx.strokeStyle = 'rgba(180, 220, 255, 0.85)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 2]);
    ctx.beginPath();
    ctx.arc(x, y, SHIP_DRAW_RADIUS + 2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  // v2M7.3: shield halo first (drawn beneath the silhouette so it reads
  // as a soft outer aura). Skipped when shieldPct == 0. Halo is drawn in
  // screen space — radius derived from the ship's class scale × the
  // canonical ~12 px ship length × 0.6 (per GRAPHICS §4.5 ratio).
  const scale = classScale(input.classLevel);
  if (input.shieldPct > 0) {
    drawShieldHalo(ctx, x, y, input.shieldPct, scale);
  }

  // Body — per-race silhouette per GRAPHICS §4.3, scaled by class
  // (v2M7.2.5) and overlaid with a role-specific add-on so a Scout reads
  // differently from a Coloniser even though both share the race base.
  // Coordinate convention for each draw helper: ship centre at (0,0),
  // nose pointing UP (-y). v2M7.3 wires heading: silhouette is drawn
  // pointing up canonically, then rotated by `heading + π/2` so a
  // heading of 0 (= +x axis, world) renders the ship pointing right.
  const role = chassisRoleFor(input.chassisId);
  // v2M7.3 cloak alpha tightened to 0.4 per GRAPHICS §9.2 spec (was 0.5
  // in v2M7.1 placeholder). Radial distortion ripple from §9.2 deferred
  // — needs offscreen canvas distortion mapping; not worth v2M7.3 scope.
  ctx.save();
  if (cloaked) ctx.globalAlpha = 0.4;
  ctx.translate(x, y);
  ctx.rotate(input.heading + Math.PI / 2);
  ctx.scale(scale, scale);
  // Space structures bypass the per-race silhouette entirely — they're
  // stationary kit, not race-themed combat ships. v2M7.9b adds per-kind
  // silhouettes (Extractor / Repair Yard) per GRAPHICS §6.2; ODP /
  // Minefield / Warp Gate (v3M2) and unknown chassis fall through to
  // the hexagonal placeholder.
  if (role === 'space-structure') {
    drawSpaceStructure(ctx, accent, input.chassisId);
    ctx.restore();
    return;
  }
  // v3M17 squadrons — distinct cluster glyph reads as multiple fighters
  // in formation rather than a single ship. Bypasses the per-race
  // silhouette like space-structures do.
  if (role === 'squadron') {
    drawSquadronCluster(ctx, accent, hullStrokeColour(input.hullPct));
    ctx.restore();
    return;
  }
  // MFix14 — commissioned-bitmap fast path with three-tier resolution.
  //
  //   (1) Pre-coloured variant from the artist (Option C — baked
  //       accent ring per empire colour). Resolved by mapping the
  //       empire's hex colour to a palette ID via paletteIdFromHex.
  //   (2) Neutral sprite + runtime multiply-tint (Option B — MFix13
  //       fallback path). Used when the empire colour isn't in the
  //       palette (legacy saves with free-RGB v4M1.9 colours) or when
  //       no pre-coloured variant has been authored for this
  //       (race, chassis, colourId) yet.
  //   (3) Procedural per-race silhouette (v2M7 baseline).
  //
  // The colorId-from-hex lookup is O(8); cached lookups in the
  // per-color sprite cache and per-color-tinted sprite cache cost
  // one Map.get apiece per ship per frame.
  const colorId = paletteIdFromHex(accent);
  const coloredSprite = colorId
    ? getColoredShipSprite(input.race, input.chassisId, colorId)
    : null;
  const rawSprite = coloredSprite ?? getShipSprite(input.race, input.chassisId);
  if (rawSprite) {
    // If we resolved a pre-coloured variant, no further tinting —
    // the artist's baked accent ring IS the ownership signal. If we
    // fell through to the neutral sprite, run the multiply-tint pass
    // so same-race ships of different empires stay distinguishable.
    const sprite = coloredSprite
      ?? getTintedShipSprite(input.race, input.chassisId, accent)
      ?? rawSprite;
    const half = sprite.width * SHIP_SPRITE_RENDER_SCALE * 0.5;
    ctx.drawImage(
      sprite as unknown as CanvasImageSource,
      -half,
      -half,
      half * 2,
      half * 2,
    );
    drawRoleAddOn(ctx, role);
    ctx.restore();
    return;
  }
  // v2M7.3 hull tint: silhouette stroke shifts from Hull-highlight →
  // Hull-red as hullPct drops. Each per-race silhouette takes the
  // outline colour rather than computing it itself, so the lerp logic
  // stays in one place.
  const outline = hullStrokeColour(input.hullPct);
  switch (input.race) {
    case 'RACE-AA':
      drawTerranSilhouette(ctx, accent, outline);
      break;
    case 'RACE-BB':
      drawKargathSilhouette(ctx, accent, outline);
      break;
    case 'RACE-CC':
      drawRemanSilhouette(ctx, accent, outline);
      break;
    case 'RACE-DD':
    case 'RACE-EE':
      // v2-deferred races — chevron fall-back so a stray instance at
      // least renders as something. Forward-compat: real silhouettes
      // land when those races ship.
      drawChevronFallback(ctx, accent);
      break;
  }
  drawRoleAddOn(ctx, role);
  ctx.restore();
}

/**
 * Soft outer halo at Shield-blue, alpha proportional to `shieldPct`.
 * Drawn in screen space (not under the silhouette's transform) so the
 * radial gradient reads as a clean circle regardless of heading
 * rotation. Radius scales with class so a Class 6 capital's halo is
 * visibly larger than a Scout's.
 *
 * Per GRAPHICS §4.5: `radius = dimensions.length × 0.6`, alpha =
 * `shieldPct × 0.25`. v2M7.3 substitutes `12 × scale` for the
 * dimensions.length (12 = canonical render length). When Ships.xlsx
 * Length lands, the call site can pass real dimensions and this stays
 * correct.
 *
 * Shield-impact flash (3 frames solid alpha per §7.2) deferred — needs
 * a transient render-state field that v2M7.4 weapon-tracer pass will
 * introduce. For now the halo is steady-state per shieldPct.
 */
function drawShieldHalo(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  shieldPct: number,
  scale: number,
): void {
  const radius = 12 * 0.6 * scale;
  // Hex → RGB once for the gradient stops.
  const r = parseInt(SHIELD_BLUE.slice(1, 3), 16);
  const g = parseInt(SHIELD_BLUE.slice(3, 5), 16);
  const b = parseInt(SHIELD_BLUE.slice(5, 7), 16);
  const peakAlpha = shieldPct * 0.25;
  const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
  // Inner core transparent, peak just inside the rim, fade to zero at edge.
  grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`);
  grad.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, ${peakAlpha * 0.6})`);
  grad.addColorStop(0.95, `rgba(${r}, ${g}, ${b}, ${peakAlpha})`);
  grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
  ctx.save();
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * Role-specific overlay painted on TOP of the per-race silhouette.
 * Race-agnostic — same add-on shape regardless of which race base it
 * sits over. Kept chunky enough to read at the smallest class scale
 * (0.85×). Stroke = HULL_HIGHLIGHT for visual continuity with the
 * silhouette outlines.
 */
function drawRoleAddOn(ctx: CanvasRenderingContext2D, role: ShipRole): void {
  ctx.fillStyle = HULL_HIGHLIGHT;
  ctx.strokeStyle = HULL_HIGHLIGHT;
  ctx.lineWidth = 1;
  switch (role) {
    case 'scout':
      // Sensor antenna — thin vertical line above the prow.
      ctx.beginPath();
      ctx.moveTo(0, -8);
      ctx.lineTo(0, -11);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, -11, 1, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'colony':
      // Cargo bulge — fat rectangle slung under the stern.
      ctx.beginPath();
      ctx.rect(-3, 5, 6, 3);
      ctx.fill();
      ctx.stroke();
      break;
    case 'utility':
      // Manipulator arm — line extending from the right side mid-hull.
      ctx.beginPath();
      ctx.moveTo(4, 0);
      ctx.lineTo(8, -2);
      ctx.lineTo(9, 0);
      ctx.stroke();
      break;
    case 'combat-light':
      // Single weapon mount — small dot at the prow.
      ctx.beginPath();
      ctx.arc(0, -6, 1.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'combat':
      // Dual weapon mounts — two dots flanking the prow.
      ctx.beginPath();
      ctx.arc(-3, -5, 1.2, 0, Math.PI * 2);
      ctx.arc(3, -5, 1.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'capital':
      // Triple weapon mounts + heavier prow plate. Scale already makes
      // capitals visibly larger; the mounts emphasise "warship".
      ctx.beginPath();
      ctx.arc(-4, -5, 1.4, 0, Math.PI * 2);
      ctx.arc(0, -7, 1.4, 0, Math.PI * 2);
      ctx.arc(4, -5, 1.4, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'space-structure':
      // Handled in the dispatcher above (bypasses race silhouette).
      break;
  }
}

/**
 * Second-level classifier for the `space-structure` role. v2M7.9b gives
 * per-chassis silhouettes for the kinds we ship in v1; ODP / Minefield /
 * Warp Gate (v3M2) and unmatched chassis fall through to the generic
 * placeholder.
 */
type SpaceStructureKind = 'extractor' | 'repair-yard' | 'placeholder';

function spaceStructureKindFor(chassisId: string): SpaceStructureKind {
  // Asteroid Mining Outpost + Gas Giant Refinery → "extractor" silhouette.
  if (/(Outpost|Refinery)/i.test(chassisId)) return 'extractor';
  // Repair Yard. Aura pulse is rendered as a separate pass in system-view.
  if (/RepairYard/i.test(chassisId)) return 'repair-yard';
  return 'placeholder';
}

/**
 * Dispatch a space-structure silhouette by chassis. Called from drawShip
 * when `chassisRoleFor` returns 'space-structure'.
 */
function drawSpaceStructure(
  ctx: CanvasRenderingContext2D,
  accent: string,
  chassisId: string,
): void {
  // v4M4.b — commissioned-bitmap fast path. If a sprite has been
  // loaded for this chassis, blit it instead of running the procedural
  // per-kind silhouette. The caller's class-scale transform is already
  // in effect; `getStructureSpriteScale` (v4M4.d) pulls the 64 px
  // source down to ~14 px on-canvas baseline, with per-chassis
  // overrides (e.g. Sensor Buoy = 5×) applied transparently. The
  // empire-accent ring + halo render separately in `system-view.ts`
  // (around the ship sprite, not inside its draw frame), so the
  // bitmap doesn't need to bake in any race / empire colouring.
  const sprite = getSpaceStructureSprite(chassisId);
  if (sprite) {
    const half = sprite.width * getStructureSpriteScale(chassisId) * 0.5;
    ctx.drawImage(
      sprite as unknown as CanvasImageSource,
      -half,
      -half,
      half * 2,
      half * 2,
    );
    return;
  }
  switch (spaceStructureKindFor(chassisId)) {
    case 'extractor':
      drawExtractorSilhouette(ctx, accent);
      break;
    case 'repair-yard':
      drawRepairYardSilhouette(ctx, accent);
      break;
    case 'placeholder':
      drawSpaceStructurePlaceholder(ctx, accent);
      break;
  }
}

/**
 * Extractor silhouette per GRAPHICS §6.2 — hexagonal hub with three
 * mining arms reaching outward at 120 ° apart. Spec calls for arms
 * to point at the nearest asteroid/gas source; v2M7.9b ships static
 * 120 ° apart and leaves dynamic arm-aiming as a v3M11 polish item
 * (would require ship-draw to take environment-entity context).
 */
function drawExtractorSilhouette(ctx: CanvasRenderingContext2D, accent: string): void {
  ctx.fillStyle = accent;
  ctx.strokeStyle = HULL_HIGHLIGHT;
  ctx.lineWidth = 1.2;
  // Hexagonal hub.
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i;
    const px = Math.cos(a) * 5;
    const py = Math.sin(a) * 5;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // Three mining arms — extend outward 9 px past the hub at 0°, 120°, 240°.
  ctx.lineWidth = 1.6;
  for (let i = 0; i < 3; i++) {
    const a = (Math.PI * 2 * i) / 3;
    const cx = Math.cos(a);
    const cy = Math.sin(a);
    ctx.beginPath();
    ctx.moveTo(cx * 5, cy * 5);
    ctx.lineTo(cx * 14, cy * 14);
    ctx.stroke();
    // Arm tip — small filled dot suggesting collector head.
    ctx.fillStyle = HULL_HIGHLIGHT;
    ctx.beginPath();
    ctx.arc(cx * 14, cy * 14, 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = accent;
  }
}

/**
 * Repair Yard silhouette per §6.2 — rectangular cradle with two parallel
 * spars enclosing an empty bay where a docked ship would sit. Pulsing
 * aura ring is drawn as a separate pass in system-view.ts so this
 * function stays tick-independent.
 */
function drawRepairYardSilhouette(ctx: CanvasRenderingContext2D, accent: string): void {
  ctx.fillStyle = accent;
  ctx.strokeStyle = HULL_HIGHLIGHT;
  ctx.lineWidth = 1.2;
  // Outer cradle — two parallel bars top + bottom, shorter end caps left + right.
  // Filled interior so the silhouette reads as a solid mass.
  ctx.beginPath();
  ctx.rect(-9, -6, 18, 12);
  ctx.fill();
  ctx.stroke();
  // Cut out the centre to suggest the docking bay.
  ctx.fillStyle = '#020408'; // matches canvas backdrop
  ctx.beginPath();
  ctx.rect(-5, -3, 10, 6);
  ctx.fill();
  ctx.fillStyle = accent;
  // Connecting struts — two short lines on the long axis suggesting
  // dock arms.
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-9, 0);
  ctx.lineTo(-5, 0);
  ctx.moveTo(5, 0);
  ctx.lineTo(9, 0);
  ctx.stroke();
}

/**
 * Generic space-structure placeholder — hexagonal hub with cross-axis
 * spokes. Used for any chassis that doesn't match a v2M7.9b kind (ODP /
 * Minefield / Warp Gate land in v3M2 with their own silhouettes).
 */
function drawSpaceStructurePlaceholder(
  ctx: CanvasRenderingContext2D,
  accent: string,
): void {
  ctx.fillStyle = accent;
  ctx.strokeStyle = HULL_HIGHLIGHT;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i;
    const px = Math.cos(angle) * 5;
    const py = Math.sin(angle) * 5;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(5, 0);
  ctx.lineTo(8, 0);
  ctx.moveTo(-5, 0);
  ctx.lineTo(-8, 0);
  ctx.stroke();
}

/**
 * v3M17 — Squadron cluster glyph. Three small race-accented triangles
 * arranged in a tight delta formation reading as multiple fighters in
 * formation rather than a single ship. Bypasses per-race silhouette
 * detail at this scale because squadron triangles are too small to
 * carry per-race detail; race identity comes from the accent colour
 * alone (matches the v2M7 read-at-a-glance principle: race = colour).
 *
 * Coordinate convention: centre at (0, 0), nose at -y. Each triangle is
 * ~3 px tall, drawn in screen-space pixels (caller's transform already
 * includes class scale + heading rotation). Hull-tint applied to the
 * outer stroke so a damaged squadron's triangles shift toward red as
 * `liveFighterCount` drops the underlying hull pool.
 *
 * Future polish (v3M11): could vary triangle count by `liveFighterCount`
 * (5 fighters → 5 triangles, dropping as fighters die). For v3M17.4
 * the static 3-triangle glyph is the minimum readable representation.
 */
function drawSquadronCluster(
  ctx: CanvasRenderingContext2D,
  accent: string,
  outline: string,
): void {
  ctx.fillStyle = accent;
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1;
  // Three triangles in a tight delta — lead nose at front (-y),
  // wingmen flanking and slightly behind. Each triangle ~3 px tall.
  const triangles: Array<{ cx: number; cy: number }> = [
    { cx: 0, cy: -2 },   // lead
    { cx: -3, cy: 2 },   // left wingman
    { cx: 3, cy: 2 },    // right wingman
  ];
  for (const t of triangles) {
    ctx.beginPath();
    ctx.moveTo(t.cx, t.cy - 2.5);    // tip
    ctx.lineTo(t.cx - 2, t.cy + 1.5); // back-left
    ctx.lineTo(t.cx + 2, t.cy + 1.5); // back-right
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
}

/**
 * RACE-AA Terran Consortium — clean industrial. Symmetric trapezoidal
 * primary hull with paired outrigger engines flanking the stern. 1.5 px
 * stroke at Hull-highlight, filled with the race accent.
 *
 * Silhouette (centre at 0,0, nose at -y):
 *
 *      ┌───┐         narrow prow
 *      │   │
 *      │   │
 *     ┌┴───┴┐        wider mid-hull
 *     │     │
 *   ┌─┴─────┴─┐      stern with outriggers
 *   │ │   │ │
 *   └─┘   └─┘
 */
function drawTerranSilhouette(
  ctx: CanvasRenderingContext2D,
  accent: string,
  outline: string,
): void {
  ctx.fillStyle = accent;
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.5;

  // Primary hull — narrow trapezoid widening toward stern.
  ctx.beginPath();
  ctx.moveTo(-2, -7);
  ctx.lineTo(2, -7);
  ctx.lineTo(3, -1);
  ctx.lineTo(4, 5);
  ctx.lineTo(-4, 5);
  ctx.lineTo(-3, -1);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Outrigger engines — paired side rectangles at the stern.
  ctx.beginPath();
  ctx.rect(-6, 1, 2, 5);
  ctx.rect(4, 1, 2, 5);
  ctx.fill();
  ctx.stroke();
}

/**
 * RACE-BB Kargath Imperium — aggressive forward-raked. Broad prow,
 * tapering stern; weapon blisters protrude from the hull mid-section.
 * 2 px stroke (heavier) to read as armoured.
 *
 *   ┌───────┐        broad prow
 *   │       │
 *  ╔│       │╗       weapon blisters protruding
 *   │       │
 *    ╲     ╱         tapering stern
 *     └───┘
 */
function drawKargathSilhouette(
  ctx: CanvasRenderingContext2D,
  accent: string,
  outline: string,
): void {
  ctx.fillStyle = accent;
  ctx.strokeStyle = outline;
  ctx.lineWidth = 2;

  // Primary hull — broad prow tapering to narrower stern.
  ctx.beginPath();
  ctx.moveTo(-5, -6);
  ctx.lineTo(5, -6);
  ctx.lineTo(5, 0);
  ctx.lineTo(2, 6);
  ctx.lineTo(-2, 6);
  ctx.lineTo(-5, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Weapon blisters — protruding from the side mid-hull.
  ctx.beginPath();
  ctx.rect(-7, -3, 2, 3);
  ctx.rect(5, -3, 2, 3);
  ctx.fill();
  ctx.stroke();
}

/**
 * RACE-CC Reman Trust — low-signature smooth. Asymmetric curved hull
 * (no perfect mirror), no protruding weapon blisters (internal mounts).
 * 1 px stroke (light) for low-contrast read; cloak-when-set further
 * fades alpha at the caller level (cloakPhase != null).
 *
 *      ╲           asymmetric curve, slightly bulging right
 *       ╲
 *        ╲
 *     ╲   ╲
 *      ╲   ╲
 *       ────         tapered stern
 */
function drawRemanSilhouette(
  ctx: CanvasRenderingContext2D,
  accent: string,
  outline: string,
): void {
  ctx.fillStyle = accent;
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1;

  // Asymmetric curved silhouette via beziers. Right side bulges slightly
  // more than left to break the mirror; reads as "smooth predator".
  ctx.beginPath();
  ctx.moveTo(0, -7);
  // Right side — pronounced curve outward.
  ctx.bezierCurveTo(4, -5, 5, 1, 3, 6);
  // Stern — slight asymmetric base.
  ctx.lineTo(-2, 6);
  // Left side — gentler curve inward.
  ctx.bezierCurveTo(-3, 1, -2, -4, 0, -7);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

/** v2M7.1 chevron — fall-back for unknown races. */
function drawChevronFallback(ctx: CanvasRenderingContext2D, accent: string): void {
  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.moveTo(0, -6);
  ctx.lineTo(5, 5);
  ctx.lineTo(-5, 5);
  ctx.closePath();
  ctx.fill();
}
